import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowRight, ArrowUpRight } from 'lucide-react';

/**
 * KPI del rediseño Tech Premium: label chico uppercase, valor grande (sans semibold),
 * delta con flecha y color por dirección, sparkline opcional (serie única, color de
 * gráficas validado para daltonismo: --chart-1).
 */
export function StatTile({
  label,
  value,
  sub,
  delta,
  spark,
}: {
  label: ReactNode;
  value: ReactNode;
  /** Línea secundaria debajo del valor (ej. "Billed — invoices issued"). */
  sub?: ReactNode;
  /** Delta: dirección de la flecha + si esa dirección es BUENA (decide el color). */
  delta?: { text: ReactNode; dir: 'up' | 'down' | 'flat'; good?: boolean };
  /** Serie para el sparkline (se normaliza sola al alto disponible). */
  spark?: number[];
}) {
  const DeltaIcon = delta?.dir === 'up' ? ArrowUpRight : delta?.dir === 'down' ? ArrowDownRight : ArrowRight;
  const deltaColor = delta ? (delta.dir === 'flat' ? 'var(--muted)' : delta.good ? 'var(--green)' : 'var(--red)') : undefined;
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub != null && <div className="stat-sub">{sub}</div>}
      {delta && (
        <div className="stat-delta" style={{ color: deltaColor }}>
          <DeltaIcon size={13} strokeWidth={2} />
          {delta.text}
        </div>
      )}
      {spark && spark.length > 1 && <Sparkline data={spark} />}
    </div>
  );
}

/** Sparkline SVG: línea 2px + área al 10%, serie única en --chart-1 (sección 1 del manual). */
function Sparkline({ data }: { data: number[] }) {
  const w = 120;
  const h = 28;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - 2 - ((v - min) / span) * (h - 4),
  ]);
  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg className="stat-spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path d={area} fill="var(--chart-1)" opacity={0.1} />
      <path d={line} fill="none" stroke="var(--chart-1)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
